import { motion } from 'framer-motion';
import { type LucideIcon } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import AnimatedCounter from '@/components/review/AnimatedCounter';

interface KpiCardProps {
  icon: LucideIcon;
  value: number;
  suffix?: string;
  label: string;
  color: string;
  index?: number;
  formatValue?: (n: number) => string;
  decimals?: number;
}

const colorMap: Record<string, { bg: string; text: string }> = {
  orange: { bg: 'bg-orange-500/10', text: 'text-orange-500' },
  emerald: { bg: 'bg-emerald-500/10', text: 'text-emerald-500' },
  blue: { bg: 'bg-blue-500/10', text: 'text-blue-500' },
};

export default function KpiCard({
  icon: Icon,
  value,
  suffix,
  label,
  color,
  index = 0,
  decimals = 0,
}: KpiCardProps) {
  const colors = colorMap[color] ?? colorMap.blue;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.1 }}
    >
      <Card className="rounded-xl shadow-sm">
        <CardContent className="flex flex-col items-center py-3 px-2 sm:p-4 text-center gap-1">
          <div className={`rounded-full p-1.5 ${colors.bg}`}>
            <Icon className={`h-5 w-5 ${colors.text}`} />
          </div>
          <p className="text-2xl sm:text-3xl font-bold leading-tight">
            <AnimatedCounter value={value} decimals={decimals} />
            {suffix && (
              <span className="text-xs text-muted-foreground ml-0.5">{suffix}</span>
            )}
          </p>
          <p className="text-xs text-muted-foreground">{label}</p>
        </CardContent>
      </Card>
    </motion.div>
  );
}
